import { useState, useCallback } from 'react';
import { UseFormReturnType, useForm } from '@mantine/form';
import { Patient, RelatedPerson } from '@medplum/fhirtypes';
import DynamicConfigService from '@configuration/dynamic-config.service';
import { useDynamicValidation } from '@hooks/useDynamicValidation';
import { getInitialValues } from '@utils/create-patient-helpers';
import { ChoremeioFormConfig } from '@configuration/components/choremeioFields.config';
import {
  usePatientFormInitialization,
  RelationType,
} from '@hooks/usePatientFormInitialization';
import { useRelatedPeople } from './useRelatedPeople';

interface UsePatientDemographicsFormParams {
  patient: Patient | null;
  opened?: boolean;
}

interface UsePatientDemographicsFormReturn {
  form: UseFormReturnType<any>;
  requiredFields: ChoremeioFormConfig['choremeioFieldsConfig'];
  relatedPersons: Partial<Record<RelationType, RelatedPerson>>;
  setRelatedPersons: (
    relatedPersons: Partial<Record<RelationType, RelatedPerson>>
  ) => void;
  hasChanges: boolean;
  handleDiscardChanges: () => void;
  resetForm: () => void;
  changedFieldsCount: number;
  refreshRelatedPeople: () => void;
}

export const usePatientDemographicsForm = ({
  patient,
  opened = true,
}: UsePatientDemographicsFormParams): UsePatientDemographicsFormReturn => {
  const [initialFormValues, setInitialFormValues] = useState<any>(null);

  // Get configuration
  const configService = DynamicConfigService.getInstance();
  const fieldsConfig = configService.getConfig(
    'choremeioFields'
  ) as ChoremeioFormConfig;
  const requiredFields = fieldsConfig.choremeioFieldsConfig;

  const validators = useDynamicValidation(requiredFields);
  const initialValues = getInitialValues(requiredFields);

  // Initialize form
  const form = useForm({
    initialValues,
    validate: validators,
  });

  const [relatedPersons, setRelatedPersons] = useState<
    Partial<Record<RelationType, RelatedPerson>>
  >({});

  // Detect if form has changes
  const hasChanges = form.isDirty();

  // Refresh key for related people - increment to trigger refresh
  const [relatedPeopleRefreshKey, setRelatedPeopleRefreshKey] = useState(0);

  // Get related people
  const relatedPeople = useRelatedPeople({
    patientId: patient?.id || '',
    refreshKey: relatedPeopleRefreshKey,
  });

  // Function to refresh related people
  const refreshRelatedPeople = useCallback(() => {
    setRelatedPeopleRefreshKey((prev) => prev + 1);
  }, []);

  // Initialize form with patient data
  usePatientFormInitialization({
    patient,
    existingRelatedPeople: relatedPeople,
    form,
    setRelatedPersons,
    setInitialFormValues,
    opened,
  });

  //----------------------------------------------------------------------------
  // Helper function to normalize empty values for comparison
  const normalizeEmptyValue = (value: any) => {
    // Treat undefined, null, and empty string as equivalent
    if (value === undefined || value === null || value === '') {
      return '';
    }
    // Normalize whitespace-only strings
    if (typeof value === 'string' && value.trim() === '') {
      return '';
    }
    return value;
  };

  // Count changed fields (compare current with initial values, treating empty values as equal)
  const changedFieldsCount = initialFormValues
    ? Object.keys(form.values).filter((key) => {
        const currentValue = normalizeEmptyValue(
          form.values[key as keyof typeof form.values]
        );
        const initialValue = normalizeEmptyValue(
          initialFormValues[key as keyof typeof initialFormValues]
        );
        return currentValue !== initialValue;
      }).length
    : 0;

  //----------------------------------------------------------------------------
  // Handle discard changes
  const handleDiscardChanges = useCallback(() => {
    if (initialFormValues) {
      // Merge with form's initial values to ensure all fields are reset
      const resetValues = {
        ...initialValues,
        ...initialFormValues,
      };
      form.setValues(resetValues);
      form.resetDirty(resetValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, initialValues]);

  // Reset baseline - resets form values to baseline and clears the baseline
  const resetForm = useCallback(() => {
    if (initialFormValues) {
      // Reset form values to baseline before clearing it
      const resetValues = {
        ...initialValues,
        ...initialFormValues,
      };
      form.setValues(resetValues);
      form.resetDirty();
    } else {
      // If no baseline, reset to initial empty values
      form.reset();
    }
    // Clear the baseline
    setInitialFormValues(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues, initialValues]);


  return {
    form,
    requiredFields,
    relatedPersons,
    setRelatedPersons,
    hasChanges,
    handleDiscardChanges,
    resetForm,
    changedFieldsCount,
    refreshRelatedPeople,
  };
};
